import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import _ from "lodash";
import axios from "axios";
import {
  GridDataSource,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from "@mui/x-data-grid-premium";
import { GridResponse, SelectOption, BulkStringOperationConfig } from "../types";
import { CRUDHandler } from "../CRUDHandler";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface UseServerDataSourceOptions {
  apiUrl: string;
  getRowId: (row: any) => string | number;
  searchQueryRef: React.MutableRefObject<string>;
  searchValue: string;
  originalRowsRef: React.MutableRefObject<Record<GridRowId, any>>;
  rowModesModel: GridRowModesModel;
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>;
  setFieldOptions: React.Dispatch<React.SetStateAction<Record<string, SelectOption[]>>>;
  setSnackbar: (snackbar: SnackbarState) => void;
  apiRef: React.RefObject<any>;
  onSuccess?: (message: string) => void;
  onError?: (error: Error) => void;
  submitOnlyChangedFields?: boolean;
  onBeforeUpdate?: (
    originalRow: any,
    updatedRow: any,
    changedFields: Record<string, any>
  ) => any;
}

interface UseServerDataSourceReturn {
  dataSource: GridDataSource;
  loading: boolean;
  crudHandlerRef: React.MutableRefObject<CRUDHandler>;
  handleRefresh: () => void;
  handleDeleteClick: (id: GridRowId) => () => Promise<void>;
  handleBulkUpdate: (
    ids: (string | number)[],
    field: string,
    operations: BulkStringOperationConfig[]
  ) => Promise<{ success: boolean; error?: string }>;
  wrappedGetRowId: (row: any) => string | number;
}

/**
 * Hook for managing the server-side data source, including fetching, updating, and deleting rows.
 */
export function useServerDataSource({
  apiUrl,
  getRowId,
  searchQueryRef,
  searchValue,
  originalRowsRef,
  rowModesModel,
  setRowModesModel,
  setFieldOptions,
  setSnackbar,
  apiRef,
  onSuccess,
  onError,
  submitOnlyChangedFields = true,
  onBeforeUpdate,
}: UseServerDataSourceOptions): UseServerDataSourceReturn {
  const [loading, setLoading] = useState(false);
  const autosizeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize CRUD handler (using ref to avoid reinitialization)
  const crudHandlerRef = useRef<CRUDHandler>(
    new CRUDHandler({
      apiUrl,
      submitOnlyChangedFields,
      getRowId,
      onBeforeUpdate,
    })
  );

  // Update CRUD handler config when props change
  useEffect(() => {
    crudHandlerRef.current.updateConfig({
      apiUrl,
      submitOnlyChangedFields,
      getRowId,
      onBeforeUpdate,
    });
  }, [apiUrl, submitOnlyChangedFields, getRowId, onBeforeUpdate]);

  // Wrapped getRowId that handles both group rows and leaf rows
  const wrappedGetRowId = useCallback(
    (row: any): string | number => {
      // Check if this is a group row (has __groupKey__ property)
      if (row.__groupKey__ !== undefined) {
        // For group rows, create a unique ID from the group field and key
        // Handle null keys with special marker
        const keyValue =
          row.__groupKey__ === null ? "__NULL__" : row.__groupKey__;
        return `group-${row.__groupField__}-${keyValue}`;
      }
      // For leaf rows, use the original getRowId
      return row.id || getRowId(row);
    },
    [getRowId]
  );

  // Refresh data
  const handleRefresh = useCallback(() => {
    if (apiRef.current) {
      // Trigger cache clear callbacks if they exist
      if ((apiRef.current as any).clearNestedCaches) {
        (apiRef.current as any).clearNestedCaches();
      }
      apiRef.current?.dataSource.fetchRows();
    }
  }, [apiRef]);

  // Delete handler
  const handleDeleteClick = useCallback(
    (id: GridRowId) => async () => {
      if (!window.confirm("Are you sure you want to delete this record?")) {
        return;
      }

      setLoading(true);
      try {
        const result = await crudHandlerRef.current.sendDeleteRequest(id);

        if (result.success) {
          // Refresh the data source
          handleRefresh();
          setSnackbar({
            open: true,
            message: "Record deleted successfully",
            severity: "success",
          });
          if (onSuccess) {
            onSuccess("Record deleted successfully");
          }
        } else {
          throw new Error(result.error || "Delete failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to delete record";

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });

        if (onError) {
          onError(error instanceof Error ? error : new Error(errorMessage));
        }
      } finally {
        setLoading(false);
      }
    },
    [handleRefresh, setSnackbar, onSuccess, onError]
  );

  // Bulk update handler for batch string operations
  const handleBulkUpdate = useCallback(
    async (
      ids: (string | number)[],
      field: string,
      operations: BulkStringOperationConfig[]
    ): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      try {
        const result = await crudHandlerRef.current.sendBulkUpdateRequest(
          ids,
          field,
          operations
        );

        if (result.success) {
          setSnackbar({
            open: true,
            message: `Successfully updated ${ids.length} records`,
            severity: "success",
          });
          handleRefresh();
          if (onSuccess) {
            onSuccess(`Bulk update completed: ${ids.length} records`);
          }
          return { success: true };
        } else {
          throw new Error(result.error || "Bulk update failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Bulk update failed";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
        if (onError) {
          onError(error instanceof Error ? error : new Error(errorMessage));
        }
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [handleRefresh, setSnackbar, onSuccess, onError]
  );

  // DataSource definition - centralized server-side logic
  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (
        params: GridGetRowsParams
      ): Promise<GridGetRowsResponse> => {
        // Skip the API call if any filter items are incomplete (no value entered yet).
        // Operators like isEmpty/isNotEmpty don't require a value, so exclude those.
        const noValueOperators = ["isEmpty", "isNotEmpty"];
        const hasIncompleteFilters = params.filterModel?.items?.some(
          (item) => {
            if (noValueOperators.includes(item.operator)) return false;
            if (
              item.value === undefined ||
              item.value === null ||
              item.value === ""
            )
              return true;
            if (Array.isArray(item.value) && item.value.length === 0)
              return true;
            return false;
          }
        );

        if (hasIncompleteFilters) {
          const rows = Array.from(
            apiRef.current.getRowModels().values()
          ) as any[];
          const rowCount = apiRef.current.state.pagination.rowCount;
          return { rows, rowCount };
        }

        try {
          const requestBody = {
            operation: "READ",
            paginationModel: {
              page: Number(params.start) / (params.end - Number(params.start)),
              pageSize: params.end - Number(params.start),
            },
            sortModel: params.sortModel,
            filterModel: params.filterModel,
            globalSearch: searchQueryRef.current || searchValue,
            groupKeys: params.groupKeys,
            groupFields: params.groupFields,
          };

          const response = await axios.post<GridResponse>(apiUrl, requestBody, {
            headers: {
              "Content-Type": "application/json",
            },
          });

          // Store options from response if present
          if (response.data.options) {
            setFieldOptions((prev) => ({
              ...prev,
              ...response.data.options,
            }));
          }

          // Auto-size columns after fetching new data
          if (autosizeDebounceRef.current) {
            clearTimeout(autosizeDebounceRef.current);
          }
          const timeout = setTimeout(() => {
            if (apiRef.current) {
              apiRef.current.autosizeColumns({
                includeOutliers: true,
              });
            }
          }, 150);
          autosizeDebounceRef.current = timeout;

          return {
            rows: response.data.data,
            // Only return rowCount for root-level rows to prevent pagination
            // from being affected when expanding grouped rows
            rowCount:
              params.groupKeys && params.groupKeys.length > 0
                ? undefined
                : response.data.totalCount,
          };
        } catch (error) {
          const errorMessage = axios.isAxiosError(error)
            ? error.response?.data?.message || error.message
            : error instanceof Error
            ? error.message
            : "Failed to fetch data";

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });

          if (onError) {
            onError(
              axios.isAxiosError(error)
                ? new Error(errorMessage)
                : error instanceof Error
                ? error
                : new Error(errorMessage)
            );
          }

          // Return empty result on error
          return {
            rows: [],
            rowCount: 0,
          };
        }
      },
      updateRow: async (params) => {
        setLoading(true);
        try {
          let previousRowSnapshot;

          if (originalRowsRef.current[params.rowId]) {
            // Pattern 1: Table edit - use snapshot from rowEditStart event
            previousRowSnapshot = originalRowsRef.current[params.rowId];
          } else if (params.previousRow) {
            // Pattern 2: Programmatic call - use provided previousRow
            // Deep clone it to ensure consistency and prevent mutations
            previousRowSnapshot = _.cloneDeep(params.previousRow);
          } else {
            // Fallback: No snapshot and no previousRow provided
            previousRowSnapshot = undefined;
          }

          // Prepare the data using CRUD handler
          const dataToSend = crudHandlerRef.current.prepareUpdateData(
            params.rowId,
            params.updatedRow,
            previousRowSnapshot
          );

          // Send the update request
          const result = await crudHandlerRef.current.sendUpdateRequest(
            dataToSend
          );

          if (result.success) {
            setSnackbar({
              open: true,
              message: "Record updated successfully",
              severity: "success",
            });

            if (onSuccess) {
              onSuccess("Record updated successfully");
            }

            // Refresh the data source
            if (apiRef.current) {
              // Trigger cache clear callbacks if they exist
              if ((apiRef.current as any).clearNestedCaches) {
                (apiRef.current as any).clearNestedCaches();
              }
              apiRef.current?.dataSource.fetchRows();
            }

            // Set row mode to view
            setRowModesModel((prev) => ({
              ...prev,
              [params.rowId]: { mode: GridRowModes.View },
            }));

            // Clean up stored snapshot if it exists
            if (originalRowsRef.current[params.rowId]) {
              delete originalRowsRef.current[params.rowId];
            }

            return params.updatedRow;
          } else {
            throw new Error(result.error || "Save failed");
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to save record";

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });

          if (onError) {
            onError(error instanceof Error ? error : new Error(errorMessage));
          }

          // Clean up snapshot even on error (if it exists)
          if (originalRowsRef.current[params.rowId]) {
            delete originalRowsRef.current[params.rowId];
          }

          throw error;
        } finally {
          setLoading(false);
        }
      },
      // Row grouping: Get the group key for a row
      getGroupKey: (row: any): string => {
        // For grouped rows (aggregates), the group key is stored in __groupKey__
        if (row.__groupKey__ !== undefined) {
          // Handle null values with special marker
          if (row.__groupKey__ === null) {
            return "__NULL__";
          }
          return String(row.__groupKey__);
        }
        // For leaf rows, return the row ID
        return String(getRowId(row));
      },
      // Row grouping: Get the number of children for a group row
      getChildrenCount: (row: any): number => {
        // Server sends __childCount__ for group rows
        return row.__childCount__ ?? 0;
      },
    }),
    [apiUrl, getRowId, searchQueryRef, searchValue, setFieldOptions, setSnackbar, apiRef, onSuccess, onError, originalRowsRef, setRowModesModel]
  );

  // Cleanup autosize debounce on unmount
  useEffect(() => {
    return () => {
      if (autosizeDebounceRef.current) {
        clearTimeout(autosizeDebounceRef.current);
      }
    };
  }, []);

  return {
    dataSource,
    loading,
    crudHandlerRef,
    handleRefresh,
    handleDeleteClick,
    handleBulkUpdate,
    wrappedGetRowId,
  };
}
